import { memo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight } from 'lucide-react';
import TanoCharacter from '../../components/Eleve/TanoCharacter';
import { useXp } from '../../context/XpContext';
import { playCorrect, playWrong, playStreak } from '../../utils/xpSound';

/**
 * Feedback Duolingo en BOTTOM-SHEET (portail, avec backdrop) après une réponse,
 * en mode correction visible.
 * - Bonne réponse : sheet vert compact (✓ + « +X XP »), passage AUTO géré par Quiz.
 * - Mauvaise réponse : une BARRE de verdict fixée en bas, sans voile ni gel du
 *   défilement ; la correction est SUR la question, que le widget marque
 *   lui-même (correction sur place, 2026-09-21). La feuille d'avant, qui
 *   redessinait la bonne réponse à part, a été supprimée au lot 7.
 *
 * `verdict`/`xp`/`message`/`streak*` calculés en amont (Quiz.jsx, au clic
 * « Vérifier »). Aucune logique de scoring ni d'XP réelle ici.
 */
/** Particules d'éclat de verdict : étoiles (navy) ou braises (volcan), gerbe
 *  balistique depuis le bas de l'écran. Trajectoires tirées au montage —
 *  pur cosmétique, pas besoin de déterminisme. Palier de série → double gerbe. */
const VerdictBurst = ({ ambiance = 'navy', renforce = false }) => {
  const glyphes = ambiance === 'volcan' ? ['🔥', '✦', '🧡', '✨'] : ['⭐', '✨', '💫', '🌟'];
  const n = renforce ? 22 : 13;
  const parts = Array.from({ length: n }, (_, i) => {
    const angle = (-90 + (Math.random() * 130 - 65)) * (Math.PI / 180); // gerbe vers le haut
    const dist = 130 + Math.random() * 190;
    return {
      g: glyphes[i % glyphes.length],
      dx: `${Math.round(Math.cos(angle) * dist)}px`,
      dy: `${Math.round(Math.sin(angle) * dist)}px`,
      rot: `${Math.round(Math.random() * 360 - 180)}deg`,
      delay: `${renforce && i >= n / 2 ? 0.18 + Math.random() * 0.1 : Math.random() * 0.08}s`,
      size: 14 + Math.round(Math.random() * 12),
    };
  });
  return (
    <>
      <div className="eleve-edge-flash" />
      {parts.map((p, i) => (
        <span
          key={i}
          className="eleve-burst-p"
          style={{ '--dx': p.dx, '--dy': p.dy, '--rot': p.rot, '--delay': p.delay, fontSize: p.size }}
        >
          {p.g}
        </span>
      ))}
    </>
  );
};

/** Socle circulaire NAVY derrière Tano : la coque blanche et les néons se
 *  noyaient sur les fonds pastel des sheets (clair sur clair). */
const SocleTano = ({ taille, children }) => (
  <div
    className="rounded-full flex items-center justify-center flex-shrink-0"
    style={{
      width: taille + 26,
      height: taille + 26,
      background: 'radial-gradient(circle at 50% 40%, #1D3A66 0%, #0A1F3D 78%)',
      boxShadow: 'inset 0 0 18px rgba(0,0,0,.35), 0 4px 14px rgba(10,31,61,.25)',
    }}
  >
    {children}
  </div>
);

const XpAnswerFeedback = memo(function XpAnswerFeedback({
  verdict, xp, message, streakPalier = 0, streakMessage, onContinue, ambiance = 'navy',
}) {
  const { profil } = useXp();
  const skins = { plume: profil?.skin_plumage, acc: profil?.skin_accessoire, aura: profil?.skin_aura };

  // Joue le son de verdict (fanfare de série au palier). La feuille verte
  // bloque le scroll de fond ; la barre de mauvaise réponse ne bloque RIEN :
  // la correction est sur la question, l'élève doit pouvoir y aller.
  useEffect(() => {
    if (verdict) document.body.style.overflow = 'hidden';
    if (verdict) {
      if (streakPalier) playStreak(streakPalier); else playCorrect();
    } else {
      playWrong();
    }
    return () => { document.body.style.overflow = ''; };
  }, [verdict, streakPalier]);

  const backdrop = <div className="fixed inset-0 z-[10024] bg-black/40 backdrop-blur-[1px]" />;

  // ── Bonne réponse : sheet vert compact (passage auto géré par Quiz) ──
  if (verdict) {
    return createPortal(
      <>
        {backdrop}
        {/* Éclat au MOMENT de l'impact (le sheet n'est que la retombée) :
            gerbe d'étoiles/braises + flash vert périphérique, renforcés au
            palier de série. */}
        <VerdictBurst ambiance={ambiance} renforce={streakPalier > 0} />
        <div className="eleve-gamified fixed inset-x-0 bottom-0 z-[10025] xp-sheet">
          <div
            className="mx-auto w-full max-w-xl sm:max-w-2xl rounded-t-3xl border-t-4 shadow-2xl px-5 pt-3 pb-5 flex flex-col items-center gap-1"
            style={{ background: '#dcfce7', borderColor: '#16a34a' }}
          >
            {/* Fête vidéo en pause (asset à régénérer avec le visage incrusté) :
                Tano joy direct — rebrancher FeteTano quand la vidéo sera prête */}
            <SocleTano taille={streakPalier ? 145 : 130}>
              <TanoCharacter {...skins} mood="joy" reactive size={streakPalier ? 145 : 130} />
            </SocleTano>
            <div className="flex items-center gap-3">
              <span className="font-extrabold text-xl" style={{ fontFamily: 'Fredoka', color: '#15803d' }}>{message || 'Bravo !'}</span>
              <span className="xp-pop text-sm font-extrabold px-3 py-1 rounded-full text-white" style={{ background: '#16a34a' }}>
                +{xp} XP
              </span>
            </div>
            {streakPalier > 0 && streakMessage && (
              <span
                className="xp-pop mt-0.5 text-sm font-extrabold px-3 py-1 rounded-full"
                style={{ fontFamily: 'Fredoka', background: 'rgba(249,115,22,.15)', color: '#c2410c', border: '1px solid rgba(249,115,22,.4)' }}
              >
                {streakMessage}
              </span>
            )}
            {/* Continuer — plus de passage automatique (décision user) */}
            <button
              type="button"
              onClick={onContinue}
              className="w-full mt-2 flex items-center justify-center gap-1 py-3 rounded-2xl text-white font-bold border-b-4 border-green-700 active:translate-y-[2px] active:border-b-2 transition-all"
              style={{ background: '#16a34a' }}
            >
              <span>Continuer</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </>,
      document.body,
    );
  }

  // ── Mauvaise réponse, corrigée SUR PLACE : une barre, pas un panneau ──
  // Tano en petit sur une ligne avec le verdict et les XP (ses 120 px se
  // prenaient sur la question), et Continuer. Pas de voile : ce qu'il y a
  // derrière est précisément ce qu'il doit lire. Pas de « C'était : » : la
  // question marquée le dit déjà.
  return createPortal(
      <div className="eleve-gamified fixed inset-x-0 bottom-0 z-[10025] xp-sheet" data-testid="barre-verdict">
        <div
          className="mx-auto w-full max-w-xl sm:max-w-2xl rounded-t-3xl shadow-2xl bg-white overflow-hidden flex flex-col"
          style={{ boxShadow: '0 -8px 30px rgba(10,31,61,.35)' }}
        >
          <div className="flex items-center gap-3 px-4 pt-3 pb-3" style={{ background: '#fee2e2' }}>
            <SocleTano taille={52}>
              <TanoCharacter {...skins} mood="sad" reactive size={52} />
            </SocleTano>
            <div className="flex flex-col min-w-0 flex-grow">
              <span className="font-extrabold text-lg leading-tight" style={{ fontFamily: 'Fredoka', color: '#b91c1c' }}>
                Mauvaise réponse
              </span>
              {message && (
                <p className="text-xs font-bold truncate" style={{ color: '#9a3412' }}>{message}</p>
              )}
            </div>
            <span className="xp-pop text-sm font-extrabold px-3 py-1 rounded-full text-white flex-shrink-0" style={{ background: '#29B6E8' }}>
              +{xp} XP
            </span>
          </div>
          <div className="p-3">
            <button
              type="button"
              onClick={onContinue}
              className="w-full flex items-center justify-center gap-1 py-3 rounded-2xl text-white font-bold border-b-4 border-blue-700 active:translate-y-[2px] active:border-b-2 transition-all"
              style={{ background: '#3B82F6' }}
            >
              <span>Continuer</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
});

export default XpAnswerFeedback;
